import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CareServiceReportOrgansError,
  CareServiceReportOrgansRequest,
  CareServiceReportOrgansResponse,
  PostCareServiceReportOrgansError,
  PostCareServiceReportOrgansRequest,
  PostCareServiceReportOrgansResponse,
} from '@/api/model';

const KEY = {
  GET_CARE_SERVICE_REPORT_ORGANS: 'GET_CARE_SERVICE_REPORT_ORGANS',
} as const;

const request = {
  getCareServiceReportOrgans: async (
    { payload: { studyId } }: CareServiceReportOrgansRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<CareServiceReportOrgansResponse>(
      `${ENDPOINTS.FCT.CARE_SERVICE_REPORT_ORGANS.STUDY}${studyId}`,
      { signal },
    );

    return data;
  },
  postCareServiceReportOrgans: async ({ payload }: PostCareServiceReportOrgansRequest) => {
    const { data } = await fetchClient.FCT.post<PostCareServiceReportOrgansResponse>(
      ENDPOINTS.FCT.CARE_SERVICE_REPORT_ORGANS.BASE,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetCareServiceReportOrgans: (
    { payload }: CareServiceReportOrgansRequest,
    config?: Partial<
      UseQueryOptions<CareServiceReportOrgansResponse, CareServiceReportOrgansError>
    >,
  ) =>
    useQuery<CareServiceReportOrgansResponse, CareServiceReportOrgansError>({
      enabled: !!payload.studyId,
      queryFn: ({ signal }) => request.getCareServiceReportOrgans({ payload }, signal),
      queryKey: [KEY.GET_CARE_SERVICE_REPORT_ORGANS, payload.studyId],
      retry: false,
      ...config,
    }),
};

const mutation = {
  usePostCareServiceReportOrgans: () =>
    useMutation<
      PostCareServiceReportOrgansResponse,
      PostCareServiceReportOrgansError,
      PostCareServiceReportOrgansRequest
    >({
      mutationFn: request.postCareServiceReportOrgans,
    }),
};

export const careServiceReportOrgans = {
  KEY,
  mutation,
  query,
  request,
};
